import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '@/store/useStore';
import {
  useDidAggregationSucceedForLaunch,
  useIsAggregationSettledForLaunch,
} from '@/api/aggregation/aggregation-settlement-tracker';
import useGameUidParam from '@/lostHooks/gameUid/useGameUid';
import resolveLaunchGameUidFromLocation from '@/lostHooks/gameUid/resolveLaunchGameUidFromLocation';
import { isOverallStatusAllowed } from '@/utilities/constants/gameOverallStatus';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

/**
 * Returns `true` when the maintenance page should be shown instead of the game.
 *
 * The game is shown only when it is not hidden (whitelist), its overall status
 * is active, the region is not in maintenance, and a provider is present. A
 * missing provider covers the invalid-`gameUid` case.
 *
 * Gated on successful aggregation for the launch game uid so it does not flash
 * before data resolves or replace an aggregation error with maintenance. It
 * re-evaluates after a soft reload because aggregation refetches (and re-marks
 * settlement) on `gameResetRevision`.
 */
export const useIsGameInMaintenance = (): boolean => {
  const urlGameUid = useGameUidParam();
  const launchGameUid = resolveLaunchGameUidFromLocation() ?? urlGameUid;
  const aggregationSettled = useIsAggregationSettledForLaunch(launchGameUid);
  const aggregationSucceeded = useDidAggregationSucceedForLaunch(launchGameUid);

  const { loading, features } = useStore(useShallow((s) => s.customerData));

  const { maintenanceMode, hiddenTestingFlags, overallStatus } = useStore(
    useShallow((s) => ({
      maintenanceMode: s.gameStatus.maintenanceMode,
      hiddenTestingFlags: s.gameStatus.hiddenTestingFlags,
      overallStatus: s.gameStatus.overallStatus,
    })),
  );

  const provider = useStore((s) => s.gameContext.provider);

  return useMemo(() => {
    // Decide maintenance only from a successful payload. On request failure,
    // provider/overallStatus remain empty but belong to the launch-error path.
    if (!aggregationSettled || !aggregationSucceeded || loading) {
      return false;
    }

    // Hidden game: the user is whitelisted if they hold at least one of the
    // game's feature keys; otherwise the game is hidden from them.
    const isHiddenGame =
      Array.isArray(hiddenTestingFlags) &&
      hiddenTestingFlags.length > 0 &&
      hiddenTestingFlags.every((flag) => !features?.includes(flag));

    const overallStatusAllowed = isOverallStatusAllowed(overallStatus);
    const shouldShowGame =
      !isHiddenGame &&
      overallStatusAllowed &&
      !maintenanceMode &&
      Boolean(provider);

    if (!shouldShowGame && provider) {
      Logger.logRumAction({
        name: '[GW][MAINTENANCE] Game in maintenance',
        useCase: DdEventCategory.GameLaunch,
        eventType: DdEventType.Info,
        payload: {
          isHiddenGame,
          isOverallStatusAllowed: overallStatusAllowed,
          overallStatus,
          maintenanceMode,
          provider,
          aggregationSettled,
          customerData: features,
          shouldShowGame,
        },
      });
    }

    return !shouldShowGame;
  }, [
    aggregationSettled,
    aggregationSucceeded,
    loading,
    hiddenTestingFlags,
    features,
    overallStatus,
    maintenanceMode,
    provider,
  ]);
};

export default useIsGameInMaintenance;
